import { CheckCircleIcon, XIcon } from '@heroicons/react/outline';
import { Form, Input, Modal } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { IntlTelInput } from '@/components/shared/IntlTelInput';
import { notify } from '@/components/shared/notify';

import { getGlobalCountry } from '@/atoms/countryState';
import { user } from '@/atoms/user';
import { requestCallBack } from '@/services/Courses';
import { User } from '@/utils/interfaces/User';

interface CallbackModalProps {
  visible: boolean;
  onClose: () => void;
  product?: number;
  zl_uid?: string;
}

const CallbackModal: React.FC<CallbackModalProps> = ({
  visible,
  onClose,
  product,
  zl_uid,
}) => {
  const [form] = Form.useForm();
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const userData: User | undefined = useRecoilValue(user);

  const [phoneData, setPhoneData] = useState({
    phone: '',
    phoneCountryCode: '',
    phonePrefix: '',
  });
  const globalCountry = getGlobalCountry();

  useEffect(() => {
    const body = document.body;
    if (visible) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }

    return () => {
      body.style.overflow = '';
    };
  }, [visible]);
  const handleFinish = async (values: any) => {
    if (!product) {
      notify('Product ID is required for callback request.', 'warning');
    }

    const payload = {
      country_code: userData?.country_code ?? phoneData.phonePrefix,
      phone: userData?.phone ?? phoneData.phone,
      name: values.name ?? userData?.name,
      email: values.email ?? userData?.email,
      product_id: product as number,
      zl_uid: zl_uid as string,
    };

    try {
      setLoading(true);
      await requestCallBack(payload);
      form.resetFields();
      setSuccess(true);
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    onClose();
  };
  useEffect(() => {
    if (!visible || !userData) return;

    const { name, email, phone } = userData;

    const isValid = typeof name === 'string' && typeof email === 'string';

    if (isValid) form.setFieldsValue({ name, email });

    const isValidPhone = typeof phone === 'string';

    if (isValidPhone) {
      form.setFieldsValue({ name, email, phone });

      setPhoneData({
        phone,
        phonePrefix: globalCountry?.phoneCode || '91',
        phoneCountryCode: globalCountry?.countryCode?.toLowerCase() || 'in',
      });

      form.submit();
    }
  }, [visible, userData]);

  return (
    <Modal
      open={visible}
      onCancel={handleClose}
      centered
      footer={null}
      closable={false}
      closeIcon={undefined}
      width='min(90vw, 480px)'
      getContainer={false}
      destroyOnHidden
      classNames={{
        header: 'bg-grayZl-950 text-white border-b border-grayZl-700',
        content: 'p-10 w-full bg-grayZl-950 md:rounded-xl',
      }}
    >
      <div className='bg-grayZl-950 text-white rounded-lg'>
        <div className='flex justify-between'>
          <h2 className='text-lg font-semibold mb-5'>
            {success ? '' : 'Enter your Details'}
          </h2>
          <button
            className='w-6 h-6 bg-grayZl-850 rounded-full flex justify-center items-center'
            onClick={handleClose}
          >
            <XIcon className='w-4 h-4 text-white' />
          </button>
        </div>

        <AnimatePresence mode='wait'>
          {!success ? (
            <motion.div
              key='form'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Form
                form={form}
                layout='vertical'
                onFinish={handleFinish}
                className='space-y-4'
              >
                {/* Name */}
                <Form.Item
                  name='name'
                  rules={[
                    { required: true, message: 'Please enter your name' },
                  ]}
                  className='!mb-0'
                >
                  <Input
                    placeholder='Name'
                    className='bg-[#121212] text-white border border-bgDarkGray rounded px-4 py-2 placeholder:text-white/70'
                  />
                </Form.Item>

                {/* Email */}
                <Form.Item
                  name='email'
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Enter a valid email' },
                  ]}
                  className='!mb-0'
                >
                  <Input
                    placeholder='Email'
                    className='bg-[#121212] text-white border border-bgDarkGray rounded px-4 py-2 placeholder:text-white/70'
                  />
                </Form.Item>

                {/* Phone */}
                <Form.Item
                  name='phone'
                  rules={[
                    {
                      required: true,
                      message: 'Please enter your phone number',
                    },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: 'Enter a valid 10-digit phone number',
                    },
                  ]}
                  className='!mb-0'
                >
                  <IntlTelInput
                    phone={phoneData.phone ?? undefined}
                    phoneCountryCode={
                      phoneData.phoneCountryCode ||
                      globalCountry?.countryCode?.toLowerCase() ||
                      'in'
                    }
                    transparentBg={true}
                    onChange={({ phone, phoneCountryCode, phonePrefix }) => {
                      const cleanedPhone = phone.replace(/^0+/, '');
                      setPhoneData({
                        phone: cleanedPhone,
                        phoneCountryCode,
                        phonePrefix,
                      });

                      form.setFieldsValue({ phone: cleanedPhone });
                    }}
                  />
                </Form.Item>

                <Form.Item className='mt-4'>
                  <ButtonDefault
                    size={4}
                    rounded={false}
                    roundedSm={true}
                    variant={ButtonVariants.PRIMARY}
                    className='w-full p-1 mt-4'
                    htmlType='submit'
                    disabled={loading}
                  >
                    <span className='flex gap-x-2 px-4 whitespace-nowrap'>
                      {loading ? 'Sending...' : 'Talk To Us'}
                    </span>
                  </ButtonDefault>
                </Form.Item>
              </Form>
            </motion.div>
          ) : (
            <motion.div
              key='success'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className='text-center'
            >
              <div className='flex flex-col items-center justify-center'>
                <CheckCircleIcon className='w-12 h-12 text-green-500 mb-4' />
                <h3 className='text-lg font-semibold mb-1'>
                  Response Submitted
                </h3>
                <p className='text-sm text-white/70 mb-6'>
                  We will be in touch soon. Kindly check your mail.
                </p>

                <ButtonDefault
                  size={4}
                  rounded={false}
                  onClick={handleClose}
                  roundedSm={true}
                  variant={ButtonVariants.GRAY}
                  className='w-full p-1 mt-4'
                  disabled={loading}
                >
                  <span className='flex gap-x-2 px-4 whitespace-nowrap'>
                    Close
                  </span>
                </ButtonDefault>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Modal>
  );
};

export default CallbackModal;
